/*
  Scroll-reveal directive `v-reveal` (HAP-1893, parent HAP-1884 Phase 3.3).
  A subtle, fast staggered fade-up for items on list/grid pages (resources,
  products, podcasts): each item gently rises + fades in once as it scrolls
  into view, offset slightly from its neighbours so a grid cascades in rather
  than popping all at once.

  Usage:
    <Card v-reveal />            // simple fade-up on scroll-in (no stagger)
    <Card v-reveal="index" />    // staggered by its position in the v-for

  Why a directive driven by the Web Animations API (not @vueuse/motion's
  v-motion, and not a CSS class toggle):
    • Stagger needs a per-item, index-derived delay that is CAPPED so a long
      list never waits seconds for the tail — easiest to express in JS.
    • WAAPI animates opacity/transform on its own timeline and hands the
      element straight back to CSS on finish (clears the inline style), so it
      never clobbers a card's existing hover transforms or layout.
    • Reduced motion must be a true no-op here: the item has to render
      IMMEDIATELY and fully visible. The global CSS guard only collapses
      durations — it would still run the fade — so we gate in JS and never
      hide the element at all when reduced motion is on.

  Compositor-only (opacity + translateY) so reveals never reflow the page.
*/
import { DURATION, EASING } from '../helpers/motion'

const TRAVEL = 12 // px the item rises from — subtle, not a big slide
const STAGGER_STEP = 55 // ms between consecutive items in a cascade
// Reset the stagger every GROUP items so the total never exceeds
// GROUP * STEP (~330ms) and an item deep in a long list (or one scrolled to
// later, on its own) never sits waiting behind a huge accumulated delay.
const STAGGER_GROUP = 6

// Live reduced-motion check — JS-driven motion neutralizes itself the same way
// the CSS guard does (memory: HAP-1886). Under reduced motion the directive is
// a complete no-op: nothing is hidden, nothing animates.
const reducedMotionMQ =
	typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia('(prefers-reduced-motion: reduce)')
		: { matches: false }

// One shared IntersectionObserver for every revealed element on the page —
// cheaper than one observer per item. A small negative bottom margin holds the
// reveal until the item is a touch into the viewport so it animates where the
// eye is, not off-screen.
let observer = null
function getObserver() {
	if (observer) return observer
	if (typeof IntersectionObserver !== 'function') return null
	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return
				observer.unobserve(entry.target)
				reveal(entry.target)
			})
		},
		{ threshold: 0.05, rootMargin: '0px 0px -8% 0px' },
	)
	return observer
}

function reveal(el) {
	const index = el.__revealIndex || 0
	const delay = (index % STAGGER_GROUP) * STAGGER_STEP
	const anim = el.animate(
		[
			{ opacity: 0, transform: `translateY(${TRAVEL}px)` },
			{ opacity: 1, transform: 'translateY(0)' },
		],
		// fill:'both' applies the (hidden) start frame through the delay so there's
		// no flash of the un-hidden element before its turn in the stagger.
		{ duration: DURATION.base, easing: EASING.decel, delay, fill: 'both' },
	)
	el.__revealAnim = anim
	anim.onfinish = () => {
		if (el.__revealAnim !== anim) return
		// Hand the element back to CSS: drop our inline pre-hide + the WAAPI hold.
		anim.cancel()
		el.style.opacity = ''
		el.style.transform = ''
		el.style.willChange = ''
		el.__revealAnim = null
	}
}

export const revealDirective = {
	mounted(el, binding) {
		// Reduced motion: show immediately, never hide, never observe.
		if (reducedMotionMQ.matches) return
		const obs = getObserver()
		if (!obs) return // no IO support → leave the element fully visible
		el.__revealIndex = typeof binding.value === 'number' ? binding.value : 0
		// Pre-hide before paint so the item doesn't flash in before it reveals.
		el.style.opacity = '0'
		el.style.willChange = 'opacity, transform'
		obs.observe(el)
	},
	unmounted(el) {
		if (observer) observer.unobserve(el)
		if (el.__revealAnim) el.__revealAnim.cancel()
		delete el.__revealIndex
		delete el.__revealAnim
	},
}

export default revealDirective
